import axios, { AxiosError, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import * as constants from "../lib/constants";
import config from "../lib/config";

type CallAPI = <RequestBodyType, ResponseBodyType>(
  endpoint: string,
  requestType: string,
  data?: RequestBodyType,
  params?: object,
  authorizationHeader?: string,
) => Promise<AxiosResponse<ResponseBodyType>>;

const callApi: CallAPI = async <RequestBodyType, ResponseBodyType>(
  endpoint: string,
  requestType: string,
  data?: RequestBodyType,
  params?: object,
  authorizationHeader?: string,
) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (authorizationHeader) {
    headers.Authorization = authorizationHeader;
  }
  const axiosInstance = axios.create({
    timeout: config.api.timeout,
    timeoutErrorMessage: "Request timed out. Threshold: " + config.api.timeout,
    headers,
    params,
    withCredentials: authorizationHeader ? false : true,
  });

  axiosRetry(axiosInstance, {
    retries: config.api.retries,
    shouldResetTimeout: true,
    retryCondition: (error: AxiosError) => {
      return (
        constants.NETWORK_RETRY_ERRORS.includes(error.code as string) ||
        constants.HTTP_RETRY_ERRORS.includes(error.response?.status as number)
      );
    },
    retryDelay: axiosRetry.exponentialDelay, // wait one second longer per retry
  });

  switch (requestType) {
    case "GET":
      return await axiosInstance.get<ResponseBodyType>(endpoint);
    case "DELETE":
      return await axiosInstance.delete<ResponseBodyType>(endpoint);
    case "POST":
      return await axiosInstance.post<ResponseBodyType>(endpoint, data);
    default:
      throw new Error("Invalid HTTP verb/requestType");
  }
};

export default callApi;
